import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Venue, Store } from '../types';
import { useVenues } from '../hooks/useVenues';
import { ApolloError } from '@apollo/client';

interface LocationRenderProps {
  venues: Venue[];
  filteredVenues: Venue[];
  loading: boolean;
  error: ApolloError | undefined;
  searchTerm: string;
  onSelectStore: (venue: Venue, store: Store) => void;
  onBack: () => void;
  onRetry: () => void;
  onSearchChange: (searchTerm: string) => void;
}

interface LocationsProps {
  onSelectStore: (venue: Venue, store: Store) => void;
  onBack?: () => void;
  children: (props: LocationRenderProps) => React.ReactNode;
}

/**
 * Framework-agnostic Locations component using render props pattern.
 * This component handles data fetching, search logic, and state management.
 * The UI rendering is completely controlled by the consumer through render props.
 */
export const Locations = React.memo(
  ({
    onSelectStore,
    onBack = () => {},
    children,
  }: LocationsProps): React.ReactElement => {
    const { loading, venues, error, fetchVenues } = useVenues();
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
      fetchVenues();
    }, [fetchVenues]);

    // Filter venues and stores based on search term
    const filteredVenues = useMemo(() => {
      if (!venues) return [];
      if (!searchTerm) return venues; // Early return if no search term

      const lowerSearchTerm = searchTerm.toLowerCase();

      return venues.filter((venue: Venue) => {
        const venueNameMatch = venue.name
          .toLowerCase()
          .includes(lowerSearchTerm);
        const hasMatchingStores = venue.stores?.some(
          (store: Store) =>
            store.name.toLowerCase().includes(lowerSearchTerm) &&
            !store.isDeleted,
        );
        return venueNameMatch || hasMatchingStores;
      });
    }, [venues, searchTerm]);

    const handleRetry = useCallback(() => {
      fetchVenues();
    }, [fetchVenues]);

    const handleSearchChange = useCallback((newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
    }, []);

    const renderProps = useMemo(
      () => ({
        venues: venues || [],
        filteredVenues,
        loading,
        error,
        searchTerm,
        onSelectStore,
        onBack,
        onRetry: handleRetry,
        onSearchChange: handleSearchChange,
      }),
      [
        venues,
        filteredVenues,
        loading,
        error,
        searchTerm,
        onSelectStore,
        onBack,
        handleRetry,
        handleSearchChange,
      ],
    );

    return children(renderProps) as React.ReactElement;
  },
) as React.FC<LocationsProps>;
